import { useMemo, useState } from "react";
import { api, type ReadingResponse, type UserView } from "../api";
import { CardBack } from "../art";
import { TAROT78_KEYS } from "../tarotKeys";
import { LockedList, Notes, Overlay, SectionBlock, Spinner } from "../ui";

export type Tarot78Kind = "one" | "three";

const POSITIONS = ["Настоящее", "Причины", "Исход"];
const TITLES: Record<Tarot78Kind, string> = { one: "Расклад на одну карту", three: "Расклад на три карты" };
const QUESTION_PRESETS = [
  "Что происходит в моих отношениях?",
  "Как будет с деньгами?",
  "Какой мой следующий шаг?",
  "Что мне сейчас важно увидеть?",
];
const DECK_VIEW = 21;

/** Вкладка «Таро на 78 карт»: отдельный, настоящий расклад (не путать с «Таро и подсказки» — старой колодой из 22 программ). */
export function Tarot78({ onStart, onPaywall, user }: { user: UserView; onStart: (k: Tarot78Kind) => void; onPaywall: () => void }) {
  const start = (k: Tarot78Kind) => (user.tier === "free" ? onPaywall() : onStart(k));
  return (
    <div className="warm">
      <div className="pagehead">
        <h1>Таро на 78 карт</h1>
        <p>Классический расклад полной колодой Таро — для глубокого вопроса</p>
      </div>
      <div className="sheet" style={{ minHeight: "70vh" }}>
        <div className="spreadcard">
          <div>
            <h3>Расклад на одну карту</h3>
            <p>Одна карта для ответа на вопрос или понимания текущей ситуации</p>
            <button className="outline" onClick={() => start("one")}>
              Сделать расклад
            </button>
          </div>
          <CardBack className="spreadcard-back" />
        </div>
        <div className="spreadcard">
          <div>
            <h3>Расклад на три карты</h3>
            <p>Три карты: настоящее, причины и исход ситуации</p>
            <button className="outline" onClick={() => start("three")}>
              Сделать расклад
            </button>
          </div>
          <CardBack className="spreadcard-back" />
        </div>
      </div>
    </div>
  );
}

interface FlowProps {
  kind: Tarot78Kind;
  onClose: () => void;
  onPaywall: () => void;
  onLoaded: (r: ReadingResponse) => void;
}

export function Tarot78Flow({ kind, onClose, onPaywall, onLoaded }: FlowProps) {
  const [picks, setPicks] = useState<number[]>([]);
  const [data, setData] = useState<ReadingResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [question, setQuestion] = useState("");
  const [asked, setAsked] = useState(false);
  const need = kind === "three" ? 3 : 1;
  const idx = useMemo(() => Array.from({ length: DECK_VIEW }, (_, i) => i), []);

  const pick = async (i: number) => {
    if (busy || picks.includes(i)) return;
    const next = [...picks, i];
    setPicks(next);
    if (next.length < need) return;
    setBusy(true);
    try {
      const q = question.trim();
      const r = await api.reading("tarot78", { kind, picks: next.join(","), ...(q ? { question: q } : {}) });
      setData(r);
      onLoaded(r);
    } catch {
      setFailed(true);
      setPicks([]);
    } finally {
      setBusy(false);
    }
  };

  if (data) return <Tarot78Result kind={kind} data={data} onClose={onClose} onPaywall={onPaywall} />;

  if (!asked) {
    return (
      <Overlay title="Какой вопрос не даёт покоя?" onClose={onClose}>
        <div className="sheet">
          <input className="text" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Напиши свой вопрос" />
          <div className="presetgrid">
            {QUESTION_PRESETS.map((p) => (
              <button key={p} className="outline" onClick={() => setQuestion(p)}>
                {p}
              </button>
            ))}
          </div>
          <button className="primary" style={{ display: "block", width: "100%", marginTop: 16 }} onClick={() => setAsked(true)}>
            Перемешать колоду
          </button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay title={kind === "three" ? "Выбери 3 карты" : "Выбери карту"} onClose={onClose}>
      {kind === "three" && (
        <div className="slots">
          {POSITIONS.map((p, i) => (
            <div className="slot" key={p}>
              <div className="ph">{picks[i] !== undefined && <CardBack />}</div>
              {p}
            </div>
          ))}
        </div>
      )}
      {busy && <Spinner />}
      {failed && (
        <p className="center" style={{ color: "var(--on-dark)" }}>
          Не удалось открыть карты. Попробуйте ещё раз.
        </p>
      )}
      {kind === "three" ? (
        <div className="deckrows">
          {[0, 1, 2].map((r) => (
            <div className="deckrow" key={r}>
              {idx.slice(r * 7, r * 7 + 7).map((i) => (
                <button key={i} className={picks.includes(i) ? "picked" : ""} onClick={() => void pick(i)} aria-label={`Карта ${i + 1}`}>
                  <CardBack />
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="cardgrid">
          {idx.slice(0, 12).map((i) => (
            <button key={i} onClick={() => void pick(i)} aria-label={`Карта ${i + 1}`}>
              <CardBack />
            </button>
          ))}
        </div>
      )}
    </Overlay>
  );
}

function Tarot78Result({ kind, data, onClose, onPaywall }: { kind: Tarot78Kind; data: ReadingResponse; onClose: () => void; onPaywall: () => void }) {
  const doc = data.reading.doc;
  /** Индексы выпавших карт (doc.numbers.cards) → ключи файлов через TAROT78_KEYS (тот же порядок, что в backend TAROT_DECK). */
  const chosen = (doc.numbers.cards as number[] | undefined) ?? [];
  const keys = chosen.map((i) => TAROT78_KEYS[i]).filter((k): k is string => Boolean(k));

  return (
    <Overlay title={TITLES[kind]} onClose={onClose}>
      <div className="faceview">
        {keys.length === 0 ? (
          <div className="faces">
            <CardBack />
          </div>
        ) : kind === "three" ? (
          <div className="faces">
            {keys.map((key, i) => (
              <img key={i} className="tarot78-face" src={`/assets/tarot/${key}.webp`} alt="" />
            ))}
          </div>
        ) : (
          <div className="bigface">
            <img className="tarot78-face" src={`/assets/tarot/${keys[0]}.webp`} alt="" />
          </div>
        )}
      </div>
      <div className="sheetbody">
        {doc.sections.map((s) => (
          <SectionBlock key={s.id} section={s} />
        ))}
        <LockedList doc={doc} onPaywall={onPaywall} />
        <Notes doc={doc} />
      </div>
    </Overlay>
  );
}
